import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
//#region src/envelope-errors.ts
/**
* `GET /v1/admin/envelope-errors` — the current CP-side envelope error per degraded app.
*
* Producer `AdminEnvelopeErrorsController` in core-api. The read joins
* `release.app_degradations` to `release.apps_catalog` and scopes the result to the apps the
* caller's org is entitled to, so an org that never held a licence for an app never sees its
* degradation. Permission `app-permission-grant:read`.
*
* `envelopeError` is the CP's own error object, passed through as stored, so the schema bounds it
* as an object with unknown members and names none of them. Reading a member out of it is the
* consumer's job and the consumer's risk.
*
* One route, one response shape, no request body and no query. The shell's `EnvelopeErrorEntry`
* and the controller's projection agree member for member.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
*/
/** The only path in this family. No parameter. */
const ADMIN_ENVELOPE_ERRORS_PATH = `${ADMIN_API_PREFIX}/envelope-errors`;
/**
* `GET /v1/admin/envelope-errors`. Every degraded app the caller's org is entitled to, newest
* observation first. Permission `app-permission-grant:read`.
*/
const LIST_ENVELOPE_ERRORS_ROUTE = route("GET", ADMIN_ENVELOPE_ERRORS_PATH);
/**
* One degraded app and the error the CP reported for it.
*
* Owned by this package: core-api's controller projects the row inline and declares no named
* type, and the shell's `EnvelopeErrorEntry` is the consumer mirror this replaces. Every member
* is non-null: `app_degradations.envelope_error`, `first_observed_at` and `last_observed_at` are
* `NOT NULL`, and `apps_catalog.slug` and `.name` are too, so the inner join cannot produce one.
*/
const envelopeErrorEntrySchema = z.object({
	/** `release.apps_catalog.id` — the uuid, never the slug. */
	appId: z.string(),
	/** `release.apps_catalog.slug`. */
	appSlug: z.string(),
	/** `release.apps_catalog.name`, the display name. */
	appName: z.string(),
	/**
	* The CP's envelope error for this app, as the operator stored it. Its members are the CP's to
	* change, so nothing here names one; a consumer that reads a member handles its absence.
	*/
	envelopeError: z.record(z.string(), z.unknown()),
	/** ISO-8601. When the platform first saw this degradation. */
	firstObservedAt: z.string(),
	/** ISO-8601. The most recent observation, and the sort key of the response. */
	lastObservedAt: z.string()
});
/**
* `GET /v1/admin/envelope-errors` response: every entry, unpaged, ordered by `lastObservedAt`
* descending.
*/
const listEnvelopeErrorsResponseSchema = z.array(envelopeErrorEntrySchema);
/** Every route this file declares, for the package's route census. */
const envelopeErrorsRoutes = Object.freeze([LIST_ENVELOPE_ERRORS_ROUTE]);
//#endregion
export { ADMIN_ENVELOPE_ERRORS_PATH, LIST_ENVELOPE_ERRORS_ROUTE, envelopeErrorEntrySchema, envelopeErrorsRoutes, listEnvelopeErrorsResponseSchema };

//# sourceMappingURL=envelope-errors.js.map